import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import {
  isValidEpisodeCount,
  EPISODE_COUNT_MIN,
  EPISODE_COUNT_MAX,
  GenerationStatus,
  validateCourseTitle,
  validateTopicPrompt,
  COURSE_TITLE_MIN,
  COURSE_TITLE_MAX,
  TOPIC_PROMPT_MIN,
  TOPIC_PROMPT_MAX,
} from '@courseai/domain';
import {
  GET_AGENT_PROFILE_OPTIONS,
  AGENT_GENERATE_COURSE,
  GET_AGENT_GENERATION_REQUEST,
  type AgentProfileOptionsData,
  type AgentProfileOptionsVariables,
  type AgentGenerateCourseData,
  type AgentGenerateCourseVariables,
  type GetAgentGenerationRequestData,
  type GetAgentGenerationRequestVariables,
  type AgentGenerationRequest,
} from '../graphql/agent';
import { AgentStructureReview } from './AgentStructureReview';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// How often (ms) the section polls getGenerationRequest while a proposal is
// still being researched (PENDING/RUNNING), and how long it keeps polling
// before it stops and points the creator at the Generaciones tab instead. The
// agent runs asynchronously; polling is a lightweight way to surface the READY
// proposal in-place without wiring a subscription into this section.
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

interface AgentGenerateSectionProps {
  readonly projectId: string;
  /** Called after a structure is approved so the caller can refresh its list. */
  onCourseCreated?(): Promise<void> | void;
}

// The section's phase: collecting the topic prompt, waiting for the agent's
// proposal, reviewing/editing the proposal, or done (structure approved).
type Phase = 'form' | 'generating' | 'review' | 'approved';

// Agent course-generation flow: submit a topic prompt via `agentGenerateCourse`,
// wait for the Agent_Service to research and propose a 3–12 episode structure,
// then review/edit and approve that structure via `approveCourseStructure`
// (which triggers script generation) or reject it and request a fresh proposal
// (Req 5.2, 5.3, 5.5). Rendered inside the Cursos tab for owners/editors; the
// server enforces the role, this mirrors it for UX.
//
// This component owns only the STARTING of a new generation and the in-session
// hand-off to review once the proposal is READY. Resuming a proposal from a
// previous session — and tracking approved courses' generation progress — lives
// in the Drafts tab/detail (DraftsTab / DraftDetail).
export function AgentGenerateSection({
  projectId,
  onCourseCreated,
}: AgentGenerateSectionProps): ReactNode {
  const client = useApolloClient();
  const { t } = useT();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [phase, setPhase] = useState<Phase>('form');

  // Topic-prompt form state.
  const [title, setTitle] = useState('');
  const [topicPrompt, setTopicPrompt] = useState('');
  const [focusAreas, setFocusAreas] = useState('');
  const [episodeCountText, setEpisodeCountText] = useState('6');
  const [voiceProfileId, setVoiceProfileId] = useState('');
  const [artStyleProfileId, setArtStyleProfileId] = useState('');
  const [formError, setFormError] = useState<string | undefined>(undefined);

  // The in-flight / completed agent request driving the review hand-off.
  const [request, setRequest] = useState<AgentGenerationRequest | undefined>(undefined);
  const [reviewError, setReviewError] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);

  // Load the project's Voice/Art profiles for the selectors and preselect each
  // type's default (Req 10.6). The agent operation does not bind these yet, but
  // surfacing them lets the creator pick the intended voice/style up front.
  const { data: profileData } = useQuery<AgentProfileOptionsData, AgentProfileOptionsVariables>(
    GET_AGENT_PROFILE_OPTIONS,
    { variables: { projectId }, fetchPolicy: 'cache-and-network' },
  );

  const voiceOptions = profileData?.getProject?.voiceProfiles ?? [];
  const artOptions = profileData?.getProject?.artStyleProfiles ?? [];

  // Preselect the default profile of each type once the options arrive, unless
  // the creator has already chosen one.
  useEffect(() => {
    if (voiceProfileId === '' && voiceOptions.length > 0) {
      const preferred = voiceOptions.find((option) => option.isDefault) ?? voiceOptions[0];
      if (preferred !== undefined) {
        setVoiceProfileId(preferred.voiceProfileId);
      }
    }
  }, [voiceOptions, voiceProfileId]);

  useEffect(() => {
    if (artStyleProfileId === '' && artOptions.length > 0) {
      const preferred = artOptions.find((option) => option.isDefault) ?? artOptions[0];
      if (preferred !== undefined) {
        setArtStyleProfileId(preferred.artStyleProfileId);
      }
    }
  }, [artOptions, artStyleProfileId]);

  const [agentGenerate, { loading: submitting }] = useMutation<
    AgentGenerateCourseData,
    AgentGenerateCourseVariables
  >(AGENT_GENERATE_COURSE);

  const titleHint = t('agent.field.titleHint', { min: COURSE_TITLE_MIN, max: COURSE_TITLE_MAX });
  const promptHint = t('agent.field.topicHint', { min: TOPIC_PROMPT_MIN, max: TOPIC_PROMPT_MAX });

  // Reset everything back to the empty topic-prompt form.
  const resetToForm = useCallback(() => {
    setPhase('form');
    setRequest(undefined);
    setReviewError(undefined);
    setFormError(undefined);
    setNote(undefined);
  }, []);

  // Poll getGenerationRequest until the proposal is READY (render the review
  // table) or FAILED (surface the error, Req 5.8). Runs only while generating.
  const pollDeadlineRef = useRef<number>(0);
  useEffect(() => {
    if (phase !== 'generating' || request === undefined) {
      return;
    }

    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    let cancelled = false;

    const timer = setInterval(() => {
      if (cancelled) {
        return;
      }
      if (Date.now() > pollDeadlineRef.current) {
        clearInterval(timer);
        setNote(t('agent.pollTimeout'));
        return;
      }
      void (async () => {
        try {
          const result = await client.query<
            GetAgentGenerationRequestData,
            GetAgentGenerationRequestVariables
          >({
            query: GET_AGENT_GENERATION_REQUEST,
            variables: { projectId, generationRequestId: request.generationRequestId },
            fetchPolicy: 'network-only',
          });
          if (cancelled) {
            return;
          }
          const latest = result.data.getGenerationRequest;
          if (latest === null) {
            return;
          }
          if (latest.status === GenerationStatus.READY) {
            clearInterval(timer);
            setRequest(latest);
            setPhase('review');
          } else if (latest.status === GenerationStatus.FAILED) {
            clearInterval(timer);
            setRequest(latest);
            setReviewError(latest.errorDescription ?? t('agent.generationFailed'));
            setPhase('form');
          }
        } catch {
          // Transient poll error — keep trying until the deadline.
        }
      })();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [phase, request, projectId, client, t]);

  // Submit the topic prompt. Composes the focus areas into the prompt so the
  // agent can weight them, validates title/prompt with the shared domain
  // validators for immediate feedback, then calls agentGenerateCourse and moves
  // to the generating phase to poll for the proposal (Req 5.1, 5.2).
  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(undefined);
    setReviewError(undefined);
    setNote(undefined);

    const trimmedFocus = focusAreas.trim();
    const composedPrompt =
      trimmedFocus.length > 0
        ? `${topicPrompt.trim()}\n\n${t('agent.focusAreasPrefix')}: ${trimmedFocus}`
        : topicPrompt;

    const titleResult = validateCourseTitle(title);
    if (!titleResult.ok) {
      setFormError(t('agent.titleInvalid', { min: COURSE_TITLE_MIN, max: COURSE_TITLE_MAX }));
      return;
    }
    const promptResult = validateTopicPrompt(composedPrompt);
    if (!promptResult.ok) {
      setFormError(t('agent.topicInvalid', { min: TOPIC_PROMPT_MIN, max: TOPIC_PROMPT_MAX }));
      return;
    }

    // The desired episode count is a hint for the agent; the agent proposes the
    // final 3–12 structure, which the creator can then edit before approval.
    const desiredCount = Number.parseInt(episodeCountText, 10);
    if (!isValidEpisodeCount(desiredCount)) {
      setFormError(
        t('agent.episodeCountInvalid', { min: EPISODE_COUNT_MIN, max: EPISODE_COUNT_MAX }),
      );
      return;
    }

    try {
      const result = await agentGenerate({
        variables: { input: { projectId, title, topicPrompt: composedPrompt } },
      });
      const created = result.data?.agentGenerateCourse;
      if (created === undefined) {
        setFormError(t('agent.startError'));
        return;
      }
      setRequest(created);
      setPhase('generating');
    } catch (err) {
      setFormError(errorMessage(err, t('agent.startError')));
    }
  }

  // Reject the proposal (Req 5.5): discard it and immediately request a fresh
  // proposal with the same inputs. No episode scripts are produced from a
  // rejected proposal — approveCourseStructure is simply never called for it.
  async function handleRegenerate(): Promise<void> {
    setReviewError(undefined);
    setNote(undefined);
    if (request === undefined) {
      return;
    }
    try {
      const result = await agentGenerate({
        variables: { input: { projectId, title, topicPrompt } },
      });
      const created = result.data?.agentGenerateCourse;
      if (created === undefined) {
        setReviewError(t('agent.regenerateError'));
        return;
      }
      setRequest(created);
      setPhase('generating');
    } catch (err) {
      setReviewError(errorMessage(err, t('agent.regenerateError')));
    }
  }

  return (
    <section className="agent-generate">
      <div className="agent-generate-header">
        <h4 className="agent-generate-title">{t('agent.title')}</h4>
        <button
          type="button"
          className="agent-generate-toggle"
          onClick={() => {
            if (expanded) {
              resetToForm();
            }
            setExpanded((open) => !open);
          }}
        >
          {expanded ? t('agent.close') : t('agent.open')}
        </button>
      </div>

      {expanded && (
        <div className="agent-generate-body">
          {phase === 'form' && (
            <form className="agent-form" onSubmit={handleSubmit}>
              <p className="agent-form-lead">
                {t('agent.lead', { min: EPISODE_COUNT_MIN, max: EPISODE_COUNT_MAX })}
              </p>

              <label>
                {t('agent.field.title')}
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (formError) setFormError(undefined);
                  }}
                  placeholder={t('agent.field.titlePlaceholder')}
                  required
                />
              </label>
              <p className="field-hint">{titleHint}</p>

              <label>
                {t('agent.field.topic')}
                <textarea
                  value={topicPrompt}
                  onChange={(e) => {
                    setTopicPrompt(e.target.value);
                    if (formError) setFormError(undefined);
                  }}
                  placeholder={t('agent.field.topicPlaceholder')}
                  rows={3}
                  required
                />
              </label>
              <p className="field-hint">{promptHint}</p>

              <label>
                {t('agent.field.focusAreas')}
                <textarea
                  value={focusAreas}
                  onChange={(e) => setFocusAreas(e.target.value)}
                  placeholder={t('agent.field.focusAreasPlaceholder')}
                  rows={2}
                />
              </label>

              <div className="agent-form-row">
                <label>
                  {t('agent.field.episodes', { min: EPISODE_COUNT_MIN, max: EPISODE_COUNT_MAX })}
                  <input
                    type="number"
                    min={EPISODE_COUNT_MIN}
                    max={EPISODE_COUNT_MAX}
                    value={episodeCountText}
                    onChange={(e) => {
                      setEpisodeCountText(e.target.value);
                      if (formError) setFormError(undefined);
                    }}
                  />
                </label>

                <label>
                  {t('agent.field.voiceProfile')}
                  <select
                    value={voiceProfileId}
                    onChange={(e) => setVoiceProfileId(e.target.value)}
                  >
                    {voiceOptions.length === 0 && (
                      <option value="">{t('agent.noVoiceProfiles')}</option>
                    )}
                    {voiceOptions.map((option) => (
                      <option key={option.voiceProfileId} value={option.voiceProfileId}>
                        {option.name}
                        {option.isDefault ? t('agent.defaultSuffix') : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  {t('agent.field.artStyle')}
                  <select
                    value={artStyleProfileId}
                    onChange={(e) => setArtStyleProfileId(e.target.value)}
                  >
                    {artOptions.length === 0 && <option value="">{t('agent.noArtStyles')}</option>}
                    {artOptions.map((option) => (
                      <option key={option.artStyleProfileId} value={option.artStyleProfileId}>
                        {option.name}
                        {option.isDefault ? t('agent.defaultSuffix') : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {reviewError && <p className="form-error">{reviewError}</p>}
              {formError && <p className="form-error">{formError}</p>}

              <div className="agent-form-actions">
                <button type="submit" disabled={submitting}>
                  {submitting ? t('agent.submitting') : t('agent.submit')}
                </button>
              </div>
            </form>
          )}

          {phase === 'generating' && (
            <div className="agent-generating">
              <p>{t('agent.generating')}</p>
              <p className="field-hint">
                {(() => {
                  // Render the "…on the {link} tab" sentence with the
                  // Drafts link substituted for the {link} placeholder.
                  const [before, after] = t('agent.generatingFollow', { link: '\u0000' }).split(
                    '\u0000',
                  );
                  return (
                    <>
                      {before}
                      <Link to={`/projects/${projectId}/drafts`}>{t('agent.generationsLink')}</Link>
                      {after}
                    </>
                  );
                })()}
              </p>
              {note && <p className="field-hint">{note}</p>}
              <button type="button" onClick={resetToForm}>
                {t('agent.cancel')}
              </button>
            </div>
          )}

          {phase === 'review' && request !== undefined && (
            <AgentStructureReview
              projectId={projectId}
              request={request}
              regenerating={submitting}
              onRegenerate={handleRegenerate}
              onDiscard={resetToForm}
              onApproved={async (created) => {
                setPhase('approved');
                setNote(t('agent.approved'));
                await onCourseCreated?.();
                // Approval kicks off a NEW job that writes the scripts and
                // creates the course. Take the user to that job's draft view so
                // they can watch the generation progress.
                navigate(`/projects/${projectId}/drafts/${created.generationRequestId}`);
              }}
            />
          )}

          {phase === 'approved' && (
            <div className="agent-approved">
              {note && <p className="field-hint">{note}</p>}
              <button type="button" onClick={resetToForm}>
                {t('agent.generateAnother')}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
